import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, Paper, Skeleton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { useNotify, useTranslate } from 'react-admin';
import marketplaceOperatorService, { WebhookEventRow } from '../../../services/marketplaceOperatorService';
import { extractErrorMessage } from '../../../services/marketplaceService';

const FILTERS = ['all', 'failed', 'processed', 'received', 'ignored'];
const fmt = (iso: string): string => new Date(iso).toLocaleString('de-DE');

/** Eingegangene PayPal-Webhooks; fehlgeschlagene Ereignisse lassen sich idempotent wiederholen (Replay). */
const OperatorWebhookEventsPanel: React.FC = () => {
    const translate = useTranslate();
    const notify = useNotify();
    const [filter, setFilter] = useState('all');
    const [events, setEvents] = useState<WebhookEventRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [busyId, setBusyId] = useState<number | null>(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            setEvents(await marketplaceOperatorService.getWebhookEvents(filter === 'all' ? undefined : filter));
        } catch (err) {
            setError(extractErrorMessage(err, translate('marketplaceOperator.common.loadError')));
        } finally {
            setLoading(false);
        }
    }, [filter, translate]);

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter]);

    const replay = async (event: WebhookEventRow) => {
        setBusyId(event.id);
        try {
            const result = await marketplaceOperatorService.replayWebhookEvent(event.id);
            notify(translate('marketplaceOperator.events.replayed', { result }), { type: 'success' });
        } catch (err) {
            notify(extractErrorMessage(err, translate('marketplaceOperator.common.actionError')), { type: 'error' });
        } finally {
            setBusyId(null);
            await load();
        }
    };

    const action = (e: WebhookEventRow) =>
        e.status === 'failed' ? (
            <Button size="small" variant="outlined" disabled={busyId === e.id} onClick={() => replay(e)}>
                {translate('marketplaceOperator.events.replay')}
            </Button>
        ) : null;
    const status = (e: WebhookEventRow) => <Chip size="small" variant="outlined" label={translate(`marketplaceOperator.events.filter.${e.status}`, { _: e.status })} />;

    return (
        <Box>
            <Typography variant="h6" sx={{ mb: 0.5 }}>{translate('marketplaceOperator.events.title')}</Typography>
            <Typography variant="body2" sx={{ color: 'var(--oa-text-2)', mb: 2, maxWidth: 760 }}>{translate('marketplaceOperator.events.intro')}</Typography>
            <ToggleButtonGroup size="small" exclusive value={filter} onChange={(_, v) => v && setFilter(v)} sx={{ mb: 2, flexWrap: 'wrap' }} aria-label={translate('marketplaceOperator.events.title')}>
                {FILTERS.map((f) => <ToggleButton key={f} value={f}>{translate(`marketplaceOperator.events.filter.${f}`)}</ToggleButton>)}
            </ToggleButtonGroup>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {loading ? (
                <Skeleton variant="rectangular" height={160} />
            ) : events.length === 0 && !error ? (
                <Alert severity="info">{translate('marketplaceOperator.events.empty')}</Alert>
            ) : (
                <>
                    <TableContainer component={Paper} variant="outlined" sx={{ display: { xs: 'none', md: 'block' } }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    {['received', 'type', 'resource', 'status', 'error', 'actions'].map((c) => <TableCell key={c}>{translate(`marketplaceOperator.events.columns.${c}`)}</TableCell>)}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {events.map((e) => (
                                    <TableRow key={e.id} data-testid={`event-${e.id}`}>
                                        <TableCell>{fmt(e.receivedAt)}</TableCell>
                                        <TableCell>{e.eventType}</TableCell>
                                        <TableCell>{e.resourceId ?? '–'}</TableCell>
                                        <TableCell>{status(e)}</TableCell>
                                        <TableCell sx={{ maxWidth: 280, wordBreak: 'break-word' }}>{e.error ?? '–'}</TableCell>
                                        <TableCell>{action(e)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Box sx={{ display: { xs: 'grid', md: 'none' }, gap: 1.5 }}>
                        {events.map((e) => (
                            <Paper key={e.id} variant="outlined" sx={{ p: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                                    <Typography sx={{ fontWeight: 600, wordBreak: 'break-word' }}>{e.eventType}</Typography>
                                    {status(e)}
                                </Box>
                                <Typography variant="body2" sx={{ color: 'var(--oa-text-2)' }}>{fmt(e.receivedAt)}</Typography>
                                {e.error && <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{e.error}</Typography>}
                                <Box sx={{ mt: 1 }}>{action(e)}</Box>
                            </Paper>
                        ))}
                    </Box>
                </>
            )}
        </Box>
    );
};

export default OperatorWebhookEventsPanel;
